import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Archive,
  ArchiveRestore,
  Calendar,
  Clock,
  Globe,
  Mail,
  MoreVertical,
  Pause,
  PenLine,
  Play,
  Settings,
  CheckCircle2,
  Users,
} from 'lucide-react'
import { Spinner } from 'react-bootstrap'

import { cn } from 'components/UI/cn'
import { NotFoundContent } from '../not-found-content'
import { WORKFLOW_STATUS } from 'models/campaign'
import apiService from 'services/api'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import {
  usePauseCampaign,
  useResumeCampaign,
} from 'features/campaigns/campaign_status_api'
import { useGetReplyThreadSummary } from 'features/campaigns/replies_api'
import { useGetCampaignSettings } from 'features/campaigns/campaign_settings_api'
import {
  useArchiveCampaign,
  useUnarchiveCampaign,
} from 'features/campaigns/campaigns_api'
import { ArchiveCampaignModal } from 'features/campaigns/ArchiveCampaignModal'
import { EditCampaignModalV2 } from 'features/campaigns/EditCampaignModalV2'
import { CampaignDashboardOverviewTab } from 'features/campaigns/CampaignDashboardOverviewTab'
import { CampaignDashboardTargetsTab } from 'features/campaigns/CampaignDashboardTargetsTab'
import { CampaignDashboardContactsTab } from 'features/campaigns/CampaignDashboardContactsTab'
import { CampaignDashboardDealsTab } from 'features/campaigns/CampaignDashboardDealsTab'
import { CampaignRepliesTab } from 'features/campaigns/CampaignRepliesTab'
import { useEntityBreadcrumbs, useHideFooter } from 'context/layout'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTitleRow,
  FBPageTitle,
  FBPageInnerTabsContainer,
} from 'components/FbUI/Page/styles'
import { Tabs } from 'components/FbUI/Tabs'

const DASHBOARD_TAB_KEYS = [
  'overview',
  'targets',
  'contacts',
  'deals',
  'replies',
] as const
type DashboardTab = (typeof DASHBOARD_TAB_KEYS)[number]

function isDashboardTab(value: string | null): value is DashboardTab {
  return (
    value !== null && (DASHBOARD_TAB_KEYS as readonly string[]).includes(value)
  )
}

function getStatusBadge(status: string): {
  label: string
  className: string
} {
  switch (status) {
    case WORKFLOW_STATUS.ACTIVE:
      return {
        label: 'Active',
        className: 'bg-brand/10 text-brand',
      }
    case WORKFLOW_STATUS.PAUSED:
      return {
        label: 'Paused',
        className: 'bg-orange-100 text-orange-700',
      }
    case WORKFLOW_STATUS.COMPLETE:
    case 'completed':
    case 'archived':
    case 'deleted':
      return {
        label: 'Complete',
        className: 'bg-gray-100 text-gray-600',
      }
    case 'launching':
      return {
        label: 'Launching',
        className: 'bg-blue-100 text-blue-700',
      }
    case 'queued':
      return {
        label: 'Queued',
        className: 'bg-blue-100 text-blue-700',
      }
    case 'failed':
      return {
        label: 'Failed',
        className: 'bg-red-100 text-red-700',
      }
    case 'pending deletion':
      return {
        label: 'Pending Deletion',
        className: 'bg-red-100 text-red-700',
      }
    default:
      return {
        label: status,
        className: 'bg-gray-100 text-fg-secondary',
      }
  }
}

function sumStepWaitDays(steps: { wait_in_days?: number }[]): number {
  if (!steps || steps.length === 0) return 0
  return steps.reduce((total, step) => total + (step.wait_in_days ?? 0), 0)
}

function formatDurationDays(days: number): string {
  if (days < 7) {
    return `${days} day${days !== 1 ? 's' : ''}`
  }
  const weeks = Math.floor(days / 7)
  const remainingDays = days % 7
  if (remainingDays === 0) {
    return `${weeks} week${weeks !== 1 ? 's' : ''}`
  }
  return `${weeks} week${weeks !== 1 ? 's' : ''} ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`
}

export function CampaignDashboardPageV2() {
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaignId = Number(params.id)

  const api = apiService()
  const { data: campaigns, isLoading: campaignsLoading } = api.useGetCampaigns()

  const campaign = useMemo(
    () => campaigns?.results.find((c) => c.id === campaignId),
    [campaigns, campaignId]
  )

  const breadcrumbs = useMemo(
    () => (campaign?.name ? [{ label: campaign.name }] : []),
    [campaign?.name]
  )
  useEntityBreadcrumbs(breadcrumbs)
  useHideFooter()

  const { data: sequence } = useGetCampaignSequence(campaignId, !!campaign)
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0

  const { data: replySummary } = useGetReplyThreadSummary({
    campaign_id: bisonCampaignId,
  })
  const { data: settings } = useGetCampaignSettings(campaignId, !!campaign)

  const pauseMutation = usePauseCampaign(campaignId)
  const resumeMutation = useResumeCampaign(campaignId)

  const [searchParams] = useSearchParams()
  // Read ?tab= once on mount so subsequent in-app tab clicks aren't
  // overridden by a stale URL. The ?thread= param is owned by
  // CampaignRepliesTab itself (it also clears it when the user dismisses
  // the linked-thread filter).
  const initialTab = useMemo<DashboardTab>(() => {
    const t = searchParams.get('tab')
    return isDashboardTab(t) ? t : 'overview'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [activeTab, setActiveTab] = useState<DashboardTab>(initialTab)
  const [showOverflowMenu, setShowOverflowMenu] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const archiveMutation = useArchiveCampaign()
  const unarchiveMutation = useUnarchiveCampaign()

  if (campaignsLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-20">
        <Spinner animation="border" />
      </div>
    )
  }

  if (!campaign) {
    return <NotFoundContent />
  }

  const campaignStatus = settings?.status || campaign.workflow_status
  const isPaused = campaignStatus === WORKFLOW_STATUS.PAUSED
  const isComplete =
    campaign.is_archived ||
    campaignStatus === 'completed' ||
    campaignStatus === 'archived' ||
    campaignStatus === 'deleted' ||
    campaignStatus === WORKFLOW_STATUS.COMPLETE
  const canControl = !isComplete
  const badge = getStatusBadge(campaignStatus)
  const emailStepsCount = sequence?.steps?.length ?? 0
  const estimatedDays = sequence?.steps ? sumStepWaitDays(sequence.steps) : 0
  const isActiveCampaign =
    campaignStatus === 'active' ||
    campaignStatus === 'queued' ||
    campaignStatus === 'launching'
  const sequenceButtonLabel =
    isComplete || isActiveCampaign ? 'View Sequence' : 'Edit Sequence'

  const tabCounts = {
    targets: campaign.contact_company_count ?? 0,
    contacts: campaign.contact_count ?? 0,
    deals: campaign.deal_count ?? 0,
    replies: replySummary?.is_available ? (replySummary?.total ?? 0) : 0,
  }

  const dashboardTabs: {
    key: DashboardTab
    title: string | React.JSX.Element
  }[] = [
    { key: 'overview', title: 'Overview' },
    {
      key: 'targets',
      title: (
        <span>
          Targets&nbsp;
          <i className="font-extralight not-italic">({tabCounts.targets})</i>
        </span>
      ),
    },
    {
      key: 'contacts',
      title: (
        <span>
          Contacts&nbsp;
          <i className="font-extralight not-italic">({tabCounts.contacts})</i>
        </span>
      ),
    },
    {
      key: 'deals',
      title: (
        <span>
          Deals&nbsp;
          <i className="font-extralight not-italic">({tabCounts.deals})</i>
        </span>
      ),
    },
    {
      key: 'replies',
      title: (
        <span>
          Replies&nbsp;
          <i className="font-extralight not-italic">({tabCounts.replies})</i>
        </span>
      ),
    },
  ]

  const handlePauseResume = () => {
    const onError = () =>
      toast.error(`Failed to ${isPaused ? 'resume' : 'pause'} campaign`)
    if (isPaused) {
      resumeMutation.mutate(bisonCampaignId, { onError })
    } else {
      pauseMutation.mutate(bisonCampaignId, { onError })
    }
  }

  return (
    <>
      {isComplete && (
        <div
          className={cn(
            'border-b',
            campaign.is_archived
              ? 'border-line bg-surface-alt'
              : 'border-emerald-200 bg-emerald-50'
          )}
        >
          <div className="mx-auto flex max-w-6xl items-center px-10 py-3">
            <div
              className={cn(
                'flex items-center gap-2 text-sm',
                campaign.is_archived ? 'text-fg-secondary' : 'text-emerald-700'
              )}
            >
              {campaign.is_archived ? (
                <Archive className="size-4" />
              ) : (
                <CheckCircle2 className="size-4" />
              )}
              {campaign.is_archived
                ? 'This campaign has been archived.'
                : 'This campaign has been completed.'}
            </div>
          </div>
        </div>
      )}

      <FBPage>
        <FBPageHeader>
          <FBPageTitleRow className="w-auto">
            <FBPageTitle>
              {campaign.name}
              {campaign.type && (
                <span className="ml-2 align-middle text-base font-normal text-fg-secondary">
                  · {campaign.type.name}
                </span>
              )}
            </FBPageTitle>
          </FBPageTitleRow>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`/campaigns/${campaignId}/sequence`)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-brand bg-brand px-3 py-1.5 text-sm text-white transition-colors hover:bg-brand-hover"
            >
              {isComplete ? (
                <Mail className="size-3.5" />
              ) : (
                <PenLine className="size-3.5" />
              )}
              <span>{sequenceButtonLabel}</span>
            </button>
            {canControl && (
              <>
                <button
                  type="button"
                  onClick={handlePauseResume}
                  disabled={pauseMutation.isPending || resumeMutation.isPending}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-line px-3 py-1.5 text-sm text-fg-subtle transition-colors hover:bg-surface-hover disabled:opacity-50"
                >
                  {isPaused ? (
                    <Play className="size-3.5" />
                  ) : (
                    <Pause className="size-3.5" />
                  )}
                  <span>{isPaused ? 'Resume' : 'Pause'}</span>
                </button>
              </>
            )}

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowOverflowMenu((prev) => !prev)}
                className="inline-flex size-9 items-center justify-center rounded-lg border border-line text-fg-secondary transition-colors hover:bg-surface-hover"
              >
                <MoreVertical className="size-4" />
              </button>

              {showOverflowMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowOverflowMenu(false)}
                  />
                  <div className="absolute right-0 top-full z-20 mt-1.5 w-48 overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setShowOverflowMenu(false)
                        setShowEditModal(true)
                      }}
                      className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                    >
                      <Settings className="size-4 text-fg-secondary" />
                      Edit Campaign
                    </button>
                    {campaign.is_archived ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowOverflowMenu(false)
                          unarchiveMutation.mutate(campaignId)
                        }}
                        disabled={unarchiveMutation.isPending}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                      >
                        <ArchiveRestore className="size-4 text-fg-secondary" />
                        Unarchive Campaign
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowOverflowMenu(false)
                          setShowArchiveConfirm(true)
                        }}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                      >
                        <Archive className="size-4 text-fg-secondary" />
                        Archive Campaign
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </FBPageHeader>

        <div className="flex flex-wrap items-center gap-6 mb-4">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              badge.className
            )}
          >
            {badge.label}
          </span>

          {campaign.launch_date && (
            <span className="flex items-center gap-1.5 text-sm text-fg-secondary">
              <Calendar className="size-4 text-brand" />
              Launch:{' '}
              <span className="font-medium text-fg">
                {new Date(campaign.launch_date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </span>
          )}

          <span className="flex items-center gap-1.5 text-sm text-fg-secondary">
            <Globe className="size-4 text-brand" />
            Sequence Name:{' '}
            <span className="font-medium text-fg">{settings?.name ?? '—'}</span>
          </span>

          <span className="flex items-center gap-1.5 text-sm text-fg-secondary">
            <Mail className="size-4 text-brand" />
            Email Steps:{' '}
            <span className="font-medium text-fg">{emailStepsCount}</span>
          </span>

          {estimatedDays > 0 && (
            <span className="flex items-center gap-1.5 text-sm text-fg-secondary">
              <Clock className="size-4 text-brand" />
              Est. Duration:{' '}
              <span className="font-medium text-fg">
                {formatDurationDays(estimatedDays)}
              </span>
            </span>
          )}

          {campaign.owner_name && (
            <span className="flex items-center gap-1.5 text-sm text-fg-secondary">
              <Users className="size-4 text-brand" />
              Owner:{' '}
              <span className="font-medium text-fg">{campaign.owner_name}</span>
            </span>
          )}
        </div>

        <FBPageInnerTabsContainer>
          <Tabs
            tabs={dashboardTabs}
            selectedTab={activeTab}
            setSelectedTab={setActiveTab}
            tabStyle={{ width: '120px' }}
          />
        </FBPageInnerTabsContainer>

        <div className="flex flex-col flex-1 min-h-0">
          {activeTab === 'overview' && (
            <CampaignDashboardOverviewTab
              campaign={campaign}
              campaignId={campaignId}
              campaignStatus={campaignStatus}
              onTabChange={(tab) => setActiveTab(tab as DashboardTab)}
            />
          )}
          {activeTab === 'targets' && (
            <CampaignDashboardTargetsTab campaign={campaign} />
          )}
          {activeTab === 'contacts' && (
            <CampaignDashboardContactsTab
              campaign={campaign}
              onSwitchToTargets={() => setActiveTab('targets')}
            />
          )}
          {activeTab === 'deals' && (
            <CampaignDashboardDealsTab campaign={campaign} />
          )}
          {activeTab === 'replies' && (
            <CampaignRepliesTab campaignId={Number(campaignId)} />
          )}
        </div>
      </FBPage>

      <EditCampaignModalV2
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        campaign={campaign}
        campaignId={campaign.id}
      />
      <ArchiveCampaignModal
        show={showArchiveConfirm}
        onHide={() => setShowArchiveConfirm(false)}
        campaignName={campaign.name}
        isActive={campaignStatus === WORKFLOW_STATUS.ACTIVE}
        onConfirm={() => {
          archiveMutation.mutate(campaignId, {
            onSuccess: () => navigate('/campaigns'),
          })
        }}
        isLoading={archiveMutation.isPending}
      />
    </>
  )
}
